import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";

/**
 * LegacyImportButton — ONE-TIME MIGRATION TOOL.
 *
 * Renders a temporary button that runs
 *   POST /api/operations/loans/import-legacy-batch/sheet_2026_07
 * behind a confirm dialog. Displays the full summary on completion and
 * disables itself once loans with import_source="sheet_2026_07" exist.
 *
 * Safe to leave in place — the underlying endpoint is idempotent and the
 * button auto-disables after the first successful import. Remove once you
 * (or the next agent) verify production is clean.
 */
const BATCH = "sheet_2026_07";
const DISPLAY_NAME = "Sheet July 2026";
const ROW_COUNT = 38;

export function LegacyImportButton() {
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(true);
  const [alreadyImported, setAlreadyImported] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [postCheck, setPostCheck] = useState(null);

  // Detect prior import so we can disable the button.
  const probe = async () => {
    try {
      const { data } = await api.get(`/operations/loans?limit=1&import_source=${BATCH}`);
      const loans = Array.isArray(data) ? data : (data.loans || data.items || []);
      // Fallback: if the endpoint ignores the filter, do a dry-run to see.
      if (!loans.length) {
        const { data: dr } = await api.post(
          `/operations/loans/import-legacy-batch/${BATCH}?dry_run=true`
        );
        setAlreadyImported(dr.duplicates_skipped >= ROW_COUNT - 1);
      } else {
        setAlreadyImported(loans.some((l) => l.import_source === BATCH));
      }
    } catch {
      /* leave alreadyImported=false — button remains available */
    } finally {
      setChecking(false);
    }
  };
  useEffect(() => { probe(); }, []);

  const run = async () => {
    setRunning(true);
    setResult(null);
    setPostCheck(null);
    try {
      const { data } = await api.post(`/operations/loans/import-legacy-batch/${BATCH}`);
      setResult(data);
      const hadInserts = data.created_funded + data.created_settled > 0;
      if (hadInserts) {
        toast.success(`Imported ${data.created_funded + data.created_settled} loans`);
        setAlreadyImported(true);
      } else {
        toast.info(`No new loans — ${data.duplicates_skipped} duplicates skipped`);
      }
      // Post-import verification pass — the user asked us to auto-check.
      await verify();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const verify = async () => {
    try {
      // 1) Dashboard KPIs
      const { data: dash } = await api.get("/operations/dashboard");
      const k = dash.kpis || {};
      // 2) Idempotency probe — 2nd dry-run should show all as duplicates
      const { data: dr } = await api.post(
        `/operations/loans/import-legacy-batch/${BATCH}?dry_run=true`
      );
      // 3) Attorney funded_volume spot-checks
      const emails = [
        "sergey.kats@katslaw.com",
        "eric@erichenrylaw.com",
        "kcpodor@gmail.com",
      ];
      const attorneys = await Promise.all(
        emails.map(async (e) => {
          try {
            const { data } = await api.get(`/operations/attorneys/${encodeURIComponent(e)}`);
            return { email: e, active: data.active_count || data.active_loans, funded: data.funded_volume || data.total_funded };
          } catch { return { email: e, error: true }; }
        })
      );
      setPostCheck({
        active_loans: k.active_loans,
        outstanding_principal: k.total_outstanding_principal,
        funded_ytd: k.funded_ytd,
        settled_loans: k.settled_loans,
        idempotent_duplicates: dr.duplicates_skipped,
        attorneys,
      });
    } catch (e) {
      setPostCheck({ error: formatApiError(e) });
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        disabled={checking || alreadyImported}
        className={
          "h-8 px-3 text-white text-xs font-medium " +
          (alreadyImported ? "bg-[#6B7280] hover:bg-[#6B7280] cursor-not-allowed" : "bg-[#7C3AED] hover:bg-[#6D28D9]")
        }
        data-testid="import-legacy-btn"
      >
        {checking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        {alreadyImported ? "Already imported (Sheet July 2026)" : `Import legacy loans (${DISPLAY_NAME})`}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!running) setOpen(v); }}>
        <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto" data-testid="import-legacy-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#0A2540]">
              Import Legacy Loans — {DISPLAY_NAME}
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4 py-2">
              <Alert className="bg-[#FFF7ED] border-[#FED7AA]">
                <AlertTriangle className="h-4 w-4 text-[#C2410C]" />
                <AlertDescription className="text-[#0A2540]">
                  <div className="font-medium">This imports 38 legacy loans. Continue?</div>
                  <div className="text-sm mt-1 text-[#374151]">
                    36 funded loans ($36,450) + 2 settled loans ($6,000) + 1 skipped
                    ("Funding In Progress"). Idempotent — safe if accidentally
                    re-run.
                  </div>
                </AlertDescription>
              </Alert>
              <div className="text-xs text-[#6B7280] rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-3 leading-relaxed">
                Each loan is created through the same firm/attorney upsert + loan-creation code path used by
                the Fund Case modal, so calculations, tier structures, payoff, dashboard, and firm
                statements all update automatically.
                <br /><br />
                Every row is tagged <code className="text-[10px] bg-white px-1 rounded">import_source: {BATCH}</code>.
              </div>
            </div>
          ) : (
            <ResultBlock result={result} postCheck={postCheck} />
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setOpen(false); setResult(null); setPostCheck(null); }}
              disabled={running}
              data-testid="import-legacy-close"
            >
              {result ? "Close" : "Cancel"}
            </Button>
            {!result ? (
              <Button
                onClick={run}
                disabled={running}
                className="bg-[#7C3AED] hover:bg-[#6D28D9]"
                data-testid="import-legacy-confirm"
              >
                {running ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Run import</>
                )}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ResultBlock({ result, postCheck }) {
  const hadInserts = result.created_funded + result.created_settled > 0;
  return (
    <div className="space-y-4 py-2" data-testid="import-legacy-result">
      <Alert className={hadInserts ? "bg-[#ECFDF5] border-[#A7F3D0]" : "bg-[#F0F7FF] border-[#BFDBFE]"}>
        <CheckCircle2 className="h-4 w-4 text-[#059669]" />
        <AlertDescription className="text-[#065F46]">
          <div className="font-semibold">
            {hadInserts
              ? `Imported ${result.created_funded + result.created_settled} loans successfully`
              : "No new loans — everything was already imported"}
          </div>
          <div className="text-xs mt-1 text-[#374151]">
            batch: <code>{result.batch}</code>
          </div>
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Funded created" value={result.created_funded} sub={`$${result.funded_sum.toLocaleString()}`} accent="green" />
        <Stat label="Settled created" value={result.created_settled} sub={`$${result.settled_sum.toLocaleString()}`} />
        <Stat label="Duplicates skipped" value={result.duplicates_skipped} />
        <Stat label="Status skipped" value={result.status_skipped} sub="Funding In Progress" />
        <Stat label="Errors" value={result.errors} accent={result.errors ? "red" : "gray"} />
        <Stat
          label="Portfolio Δ"
          value={`+$${(result.portfolio_after.outstanding_principal - result.portfolio_before.outstanding_principal).toLocaleString()}`}
          sub={`${result.portfolio_before.active_loans} → ${result.portfolio_after.active_loans} active`}
        />
      </div>

      {postCheck ? (
        <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4 space-y-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6B7280]">
            <ShieldCheck className="w-3.5 h-3.5" />
            Post-import verification
          </div>
          {postCheck.error ? (
            <div className="text-sm text-[#DC2626]">Verification failed: {postCheck.error}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <VerifyLine ok label="Dashboard active_loans" value={postCheck.active_loans} />
                <VerifyLine ok label="Outstanding principal" value={`$${(postCheck.outstanding_principal || 0).toLocaleString()}`} />
                <VerifyLine ok label="Funded YTD" value={`$${(postCheck.funded_ytd || 0).toLocaleString()}`} />
                <VerifyLine ok label="Settled loans" value={postCheck.settled_loans} />
                <VerifyLine
                  ok={postCheck.idempotent_duplicates >= 38}
                  label="Idempotency (2nd dry-run)"
                  value={`${postCheck.idempotent_duplicates}/38 duplicates`}
                />
              </div>
              <div className="border-t border-[#E5E7EB] pt-3 space-y-1">
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">
                  Attorney funded_volume
                </div>
                {postCheck.attorneys.map((a) => (
                  <div key={a.email} className="text-xs text-[#374151] flex justify-between">
                    <span className="truncate">{a.email}</span>
                    {a.error ? (
                      <span className="text-[#DC2626]">error</span>
                    ) : (
                      <span className="font-medium">{a.active} active · ${(a.funded || 0).toLocaleString()}</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, sub, accent = "gray" }) {
  const color =
    accent === "green" ? "text-[#059669]" :
    accent === "red"   ? "text-[#DC2626]" :
    "text-[#0A2540]";
  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</div>
      <div className={`font-display text-2xl font-semibold leading-none mt-1.5 ${color}`}>{value}</div>
      {sub ? <div className="text-[11px] text-[#6B7280] mt-1">{sub}</div> : null}
    </div>
  );
}

function VerifyLine({ ok, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className={ok ? "text-[#059669]" : "text-[#6B7280]"}>{ok ? "✓" : "·"}</span>
      <span className="text-[#374151] flex-1 truncate">{label}</span>
      <span className="text-[#0A2540] font-medium">{value}</span>
    </div>
  );
}
